import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import api_requests from "../../resources/api_requests";
import OrderSuccessModal from "../../modules/modals/OrderSuccessModal";
import OrderPaymentFailed from "../../modules/modals/OrderPaymentFailed";
import ConfirmClose from "../../modules/modals/ConfirmClose";
import { useNavigate } from "react-router-dom";
import utils from "../../resources/utils";
import {
  hide_loader,
  set_reciver_details,
  show_loader,
} from "../../actions/app";
import EmptyCart from "../../modules/modals/EmptyCart";
import AppNavBarWithBackAction from "../../modules/AppNavBarWithBackAction";
import CartAddressSelector from "./components/CartAddressSelector";
import CartPaymentDetails from "./components/CartPaymentDetails";
import CartOrderDetails from "./components/CartOrderDetails";
import CartAreaNotServiceable from "./components/CartAreaNotServiceable";
import { FULFILMENT_TYPE_DELIVER_NOW } from "./components/CartOrderFulfilmentDetails/CartOrderFulfilmentDetails";
import { useTranslation } from "react-i18next";

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sender_details, reciver_details } = useSelector(
    (state) => state.app,
    shallowEqual,
  );
  const [show_success_modal, set_show_success_modal] = useState(false);
  const [show_failed_modal, set_show_failed_modal] = useState(false);
  const [show_discard, set_show_discard] = useState(false);
  const [partner_fee_details, set_partner_fee_details] = useState();
  const [wallet, set_wallet] = useState();
  const [smart_allocation_config, set_smart_allocation_config] = useState();
  const [order_id, set_order_id] = useState("");
  const [pickup_otp, set_pickup_otp] = useState(true);
  const [delivery_otp, set_delivery_otp] = useState(true);
  const [is_pickup_error, set_is_pickup_error] = useState(false);
  const [is_drop_error, set_is_drop_error] = useState(false);
  const [show_empty_model, set_show_empty_model] = useState(false);
  const [partners_list, set_partners_list] = useState([]);
  const [fulfilmentType, setFulfilmentType] = useState(
    FULFILMENT_TYPE_DELIVER_NOW,
  );
  const [isTitanAI, setTitanAI] = useState(true);
  const [titanPrice, setTitanPrice] = useState(0);
  const [titanNetwork, setTitanNetwork] = useState();
  const [packageWeight, setPackageWeight] = useState("4");
  const [orderId, setOrderId] = useState("");
  const [refTrigger, setRefTrigger] = useState(uuidv4());
  const [platform_pricing, set_platform_pricing] = useState();
  const ref_id = useRef(uuidv4());
  const { t } = useTranslation();

  useEffect(() => {
    if (!_.isEmpty(sender_details) && !_.isEmpty(reciver_details)) {
      get_smart_allocation_config();
      get_wallet();
    }
    if (_.isEmpty(sender_details) && _.isEmpty(reciver_details))
      set_show_empty_model(true);
  }, []);

  useEffect(() => {
    get_platform_pricing();
  }, []);

  useEffect(() => {
    if (!_.isEmpty(smart_allocation_config) && platform_pricing) {
      get_pricing();
    }
  }, [smart_allocation_config, refTrigger, platform_pricing]);

  const get_smart_allocation_config = async () => {
    try {
      dispatch(show_loader());
      const smart_config_parent =
        await api_requests.get_smart_allocation_config();
      dispatch(hide_loader());
      set_smart_allocation_config(smart_config_parent.data);
    } catch (error) {
      dispatch(hide_loader());
    }
  };

  const get_wallet = async () => {
    dispatch(show_loader());
    try {
      const res = await api_requests.get_business_details();
      if (res?.success) {
        set_wallet(res?.data);
      }
      dispatch(hide_loader());
    } catch (error) {
      dispatch(hide_loader());
    }
  };

  const getSmartAllocatedPricing = async (data) => {
    return api_requests.get_quote(data, {
      smart_fulfill: true,
      smart_allocation_id: smart_allocation_config?.id,
      miniAppRequest: true,
    });
  };

  const getAllPartnersPricing = async (data) => {
    return api_requests.get_quote(data, {
      smart_fulfill: false,
      miniAppRequest: true,
    });
  };

  const get_platform_pricing = async () => {
    const platform_pricing = await api_requests.get_pricing();

    let pricingMap = {};

    if (platform_pricing?.success) {
      platform_pricing?.data?.items?.forEach((item) => {
        pricingMap[item.feature] = item?.plans?.[0]?.price;
      });
    }

    set_platform_pricing(pricingMap);
  };

  const get_pricing = async () => {
    const data = {
      pickup: {
        coordinates: sender_details?.coordinates,
        pincode: sender_details?.pincode,
      },
      drop: [
        {
          ref: ref_id.current,
          location: {
            coordinates: reciver_details?.coordinates,
            pincode: String(reciver_details?.pincode),
          },
          attributes: {
            cod_amount: 0,
            weight: 7500,
            volumetric_weight: 3600,
          },
        },
      ],
    };
    dispatch(show_loader());
    try {
      const smartRes = await getSmartAllocatedPricing(data);
      if (_.isEmpty(smartRes?.data)) {
        set_is_pickup_error(true);
        set_is_drop_error(true);
        return;
      }
      const filtered_pickup_now = _.filter(
        smartRes?.data,
        (item) => item.pickup_now,
      );
      if (filtered_pickup_now.length === 0) {
        set_is_pickup_error(true);
        set_is_drop_error(false);
        return;
      }
      set_is_pickup_error(false);
      const highest_price_object = _.maxBy(filtered_pickup_now, (obj) =>
        _.get(obj, "quote.price", 0),
      );
      const price = highest_price_object?.quote?.price;
      set_is_drop_error(price <= 0);
      set_partner_fee_details({
        ...highest_price_object,
        platform_fee: platform_pricing["fulfillment:parallel"] ?? 5,
      });
      setTitanNetwork({
        ...highest_price_object,
        platform_fee: platform_pricing["fulfillment:parallel"] ?? 5,
      });
      setTitanPrice(highest_price_object?.quote?.price);
      dispatch(hide_loader());

      getAllPartnersPricing(data)
        .then((allPartnersRes) => {
          const pickupNowAllPartners = _.filter(
            allPartnersRes?.data || [],
            (item) => item.pickup_now,
          );

          const filteredPartners = pickupNowAllPartners.filter(
            (item) => item.network_id !== highest_price_object?.network_id,
          );

          const partnersWithFees = filteredPartners.map((partner) => {
            let platformFee;

            if (partner?.service === "ppn-private") {
              platformFee = platform_pricing["fulfillment:ppn"] ?? 5;
            } else {
              platformFee = platform_pricing["fulfillment:partner"] ?? 5;
            }

            return {
              ...partner,
              platform_fee: platformFee,
            };
          });

          set_partners_list(partnersWithFees);
        })
        .catch((err) => {
          console.error("All Partners API failed", err);
        });
    } catch (error) {
      console.error("Smart Pricing API failed", error);
      dispatch(hide_loader());
    } finally {
      dispatch(hide_loader());
    }
  };

  const create_order = async () => {
    if (is_error_wallet) {
      navigate("/add-funds", { state: { from: "cart" } });
      return;
    }
    try {
      const drop_details = [
        {
          source_order_id: orderId,
          reference_id: ref_id.current,
          cod_amount: 0,
          bill_amount: 0,
          receiver_detail: {
            name: reciver_details?.name,
            mobile: reciver_details?.mobile,
            address: {
              id: reciver_details?.id,
              google_maps_address: reciver_details?.formatted_address,
              latitude: reciver_details?.coordinates?.latitude,
              longitude: reciver_details?.coordinates?.longitude,
              instructions: "",
              landmark: "N/A",
              coordinates: {
                latitude: reciver_details?.coordinates?.latitude,
                longitude: reciver_details?.coordinates?.longitude,
              },
              instructions_to_reach: "",
              city: reciver_details.city,
              state: reciver_details?.state,
              pincode: reciver_details?.pincode,
              formatted_address: reciver_details?.formatted_address,
              address_line_1: reciver_details?.address_name,
              address_line_2: reciver_details?.address_line1,
            },
            ...(delivery_otp && { otp: utils.generate_OTP() }),
          },
          packages: [
            {
              quantity: 1,
              volumetric_weight: 3600,
              id: 39094,
              dead_weight: packageWeight * 1000,
            },
          ],
        },
      ];
      const brand_name = wallet?.brand_name;
      dispatch(show_loader());
      const response = await api_requests.order_creation(
        {
          sender_detail: {
            name: sender_details?.name,
            mobile: sender_details?.mobile,
            address: {
              id: sender_details?.id,
              city: sender_details?.city,
              state: sender_details?.state,
              latitude: sender_details?.coordinates?.latitude,
              longitude: sender_details?.coordinates?.longitude,
              address_line_1: sender_details?.address_name,
              address_line_2: sender_details?.address_line1,
              coordinates: {
                latitude: sender_details?.coordinates?.latitude,
                longitude: sender_details?.coordinates?.longitude,
              },
              pincode: String(sender_details.pincode),
            },
            ...(pickup_otp && { otp: utils.generate_OTP() }),
          },
          poc_detail: {
            name: sender_details?.name,
            mobile: sender_details?.mobile,
          },
          trips: drop_details,
        },
        brand_name,
      );
      if (response.data) {
        set_order_id(Object.values(response.data));
        const data = {
          ids: Object.values(response.data),
          token: partner_fee_details?.token,
        };
        if (isTitanAI) {
          data["smart_allocation_id"] = smart_allocation_config?.id;
          data["fulfillment_type"] = 4;
        } else {
          data["network_id"] = partner_fee_details?.network_id;
          data["pickup_now"] = true;
          data["service"] = partner_fee_details?.service;
          data["fulfillment_type"] = 0;
        }

        await api_requests.order_fullfillment(data);
        dispatch(hide_loader());
        set_show_success_modal(true);
      }
    } catch (err) {
      const new_ref = uuidv4();
      ref_id.current = new_ref;
      setRefTrigger(new_ref);
      set_partners_list([]);
      set_partner_fee_details({});
      dispatch(hide_loader());
      set_show_failed_modal(true);
    }
  };

  const handle_back_confirm = () => {
    navigate("/dashboard");
    dispatch(set_reciver_details({}));
  };
  const btn_disable = useMemo(() => {
    let val = true;
    if (
      !_.isEmpty(sender_details) &&
      !_.isEmpty(reciver_details) &&
      !is_pickup_error &&
      !is_drop_error &&
      !_.isEmpty(sender_details?.name) &&
      !_.isEmpty(reciver_details?.name) &&
      packageWeight !== "" &&
      orderId !== "" &&
      partner_fee_details !== undefined
    ) {
      val = false;
    }
    return val;
  }, [
    sender_details,
    reciver_details,
    is_pickup_error,
    is_drop_error,
    packageWeight,
    partner_fee_details,
    orderId,
  ]);

  const gst_percent = 0.18;
  const gst_amount = useMemo(() => {
    const base_gst = parseFloat(
      partner_fee_details?.quote?.price_breakup?.total_gst_amount || 0,
    );

    const surge_gst = parseFloat(
      partner_fee_details?.quote?.price_breakup?.items?.[0]?.surge_breakup
        ?.total_surge_tax_amount || 0,
    );

    const platform_gst =
      parseFloat(partner_fee_details?.platform_fee || 0) * gst_percent;

    const val = platform_gst + base_gst + surge_gst;

    return Number(val.toFixed(2));
  }, [partner_fee_details]);

  const total_amount = useMemo(() => {
    const base_charge = parseFloat(
      partner_fee_details?.quote?.price_breakup?.base_delivery_charge || 0,
    );

    const surge_amount = parseFloat(
      partner_fee_details?.quote?.price_breakup?.items?.[0]?.surge_breakup
        ?.total_surge_base_amount || 0,
    );

    const val =
      parseFloat(partner_fee_details?.platform_fee || 0) +
      base_charge +
      surge_amount +
      parseFloat(gst_amount || 0);

    return Number(val.toFixed(2));
  }, [partner_fee_details, gst_amount]);

  const is_error_wallet = useMemo(() => {
    let val = false;
    if (!_.isEmpty(wallet) && !_.isEmpty(partner_fee_details)) {
      if (wallet.balance < total_amount) {
        val = true;
      }
    }
    return val;
  }, [wallet, total_amount, partner_fee_details]);

  return (
    <>
      <div className="h-screen bg-gray-50 flex flex-col relative p-5 pb-[300px] overflow-y-auto">
        <AppNavBarWithBackAction title={t("checkout")} />

        <div className="mt-5 p-1">
          <CartAddressSelector
            is_pickup_error={is_pickup_error}
            sender_details={sender_details}
            partner_fee_details={partner_fee_details}
            is_drop_error={is_drop_error}
            reciver_details={reciver_details}
          />

          <CartOrderDetails
            pickup_otp={pickup_otp}
            set_pickup_otp={set_pickup_otp}
            delivery_otp={delivery_otp}
            set_delivery_otp={set_delivery_otp}
            partnersList={partners_list}
            fulfilmentType={fulfilmentType}
            setFulfilmentType={setFulfilmentType}
            isTitanAI={isTitanAI}
            setTitanAI={setTitanAI}
            titanPrice={titanPrice}
            titanNetwork={titanNetwork}
            set_partner_fee_details={set_partner_fee_details}
            partner_fee_details={partner_fee_details}
            total_amount={total_amount}
            gst_amount={gst_amount}
            packageWeight={packageWeight}
            setPackageWeight={setPackageWeight}
            orderId={orderId}
            setOrderId={setOrderId}
          />
        </div>
        {!is_pickup_error && !is_drop_error && (
          <CartPaymentDetails
            total_amount={total_amount}
            create_order={create_order}
            btn_disable={btn_disable}
            is_error_wallet={is_error_wallet}
            wallet={wallet}
          />
        )}
        {(is_pickup_error || is_drop_error) && (
          <CartAreaNotServiceable
            is_pickup_error={is_pickup_error}
            is_drop_error={is_pickup_error}
          />
        )}
      </div>
      {show_success_modal && (
        <OrderSuccessModal
          close={() => set_show_success_modal(false)}
          order_id={order_id}
        />
      )}
      {show_failed_modal && (
        <OrderPaymentFailed close={() => set_show_failed_modal(false)} />
      )}
      {show_discard && (
        <ConfirmClose
          close={() => set_show_discard(false)}
          main_title={t("discard_all")}
          cancel_button={() => set_show_discard(false)}
          action_button={handle_back_confirm}
        />
      )}
      {show_empty_model && <EmptyCart />}
    </>
  );
};

export default Cart;
